import Image from 'next/image';

import { Box, DuoColorIcon, Heading, Icon, Paragraph, Text, ToggleCard } from '@zillow/constellation-10';
import { IconBuildingsFilled, IconKeyFilled, IconTrendingFilled, IconUserFilled } from '@zillow/constellation-icons';

import { ProfileType } from '@zg-rentals/ts-rental-manager-api-client';

import { PROFILE_TYPE_OPTIONS } from '../../../../../constants/AccountSettingsConstants';
import { PROFESSIONAL_PAPERWORK_SPOT_ILLUSTRATION_URL } from '../../../../../constants/DigitalLibraryConstants';

const STACK = 'stack' as const;
const GRID = 'grid' as const;

type ProfileTypeSelectionProps = {
  selectedProfileType?: ProfileType;
  onSelect: (profileType: ProfileType) => void;
  questionNumber: number;
  questionTotal: number;
  disabled?: boolean;
  layout?: typeof STACK | typeof GRID;
};

const PROFILE_TYPE_ICONS = {
  [ProfileType.LANDLORD]: IconKeyFilled,
  [ProfileType.PROPERTY_MANAGER]: IconBuildingsFilled,
  [ProfileType.AGENT]: IconUserFilled,
  [ProfileType.REAL_ESTATE_INVESTOR]: IconTrendingFilled,
};

export const ProfileTypeSelection = ({
  selectedProfileType,
  onSelect,
  questionNumber,
  questionTotal,
  disabled = false,
  layout = STACK,
}: ProfileTypeSelectionProps) => {
  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: '500',
        alignItems: 'flex-start',
        width: '100%',
      }}
    >
      <Box aria-hidden="true">
        <Image priority src={PROFESSIONAL_PAPERWORK_SPOT_ILLUSTRATION_URL} alt="" width={120} height={120} />
      </Box>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
        <Text textStyle="body-xs-bold" css={{ color: 'text.subtle' }}>
          Question {questionNumber} of {questionTotal}
        </Text>
        <Heading level={1} textStyle="heading-xs">
          Which best describes you?
        </Heading>
        <Paragraph>This helps us tailor your experience to the way you work.</Paragraph>
      </Box>
      <Box
        css={{
          display: layout === GRID ? GRID : 'flex',
          flexDirection: layout === GRID ? undefined : 'column',
          gridTemplateColumns: layout === GRID ? 'repeat(2, 1fr)' : undefined,
          gap: '400',
          width: '100%',
        }}
      >
        {PROFILE_TYPE_OPTIONS.map(({ value, label }) => {
          const isSelected = selectedProfileType === value;
          const IconComponent = PROFILE_TYPE_ICONS[value];

          return (
            <ToggleCard
              key={value}
              elevated={false}
              selected={isSelected}
              aria-pressed={isSelected}
              onClick={() => !disabled && onSelect(value)}
            >
              <Box
                css={{
                  display: 'flex',
                  alignItems: layout === GRID ? 'flex-start' : 'center',
                  flexDirection: layout === GRID ? 'column' : 'row',
                  gap: layout === GRID ? '300' : undefined,
                }}
              >
                {layout === GRID ? (
                  <DuoColorIcon tone="info" onBackground="impact">
                    <Icon size="md" render={<IconComponent />} />
                  </DuoColorIcon>
                ) : (
                  <Box
                    css={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '48px',
                      height: '48px',
                      backgroundColor: 'bg.softest',
                      borderRadius: 'md',
                    }}
                  >
                    <Icon size="md" render={<IconComponent />} />
                  </Box>
                )}

                <Text>{label}</Text>
              </Box>
            </ToggleCard>
          );
        })}
      </Box>
    </Box>
  );
};

export default ProfileTypeSelection;
